import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import { API_ENDPOINTS } from 'Constants/env';
import { MobXProviderContext } from 'mobx-react';
import { useNotificationStore } from 'Modules/notification/store';
import React from 'react';

import { RootStoreProps } from 'Stores/RootStore.types';
import { pushToGTMDataLayer } from 'Utils/analytics';
import API from '~/api';
import { getReactionAnalyticsEvent } from '../constants';
import { useUpdateMessageCache } from '../hooks/useUpdateMessageCache';
import {
  ConversationMessageReactionDto,
  ToggleReactionParams,
} from '../index.types';

const MUTATION_KEY_TOGGLE_REACTION = 'emojiReactions.toggleReaction';

/**
 * Add emoji reaction to a message
 */
const addReaction = async (
  { person: personStore }: RootStoreProps,
  conversationId: string,
  messageId: string,
  emoji: string
) => {
  try {
    await personStore.waitUntilLoggedIn();

    const response = await API.post(
      API_ENDPOINTS.ConversationMessageReactions(conversationId, messageId),
      {
        reaction: emoji,
      }
    );

    // Analytics tracking
    const analyticsEvent = getReactionAnalyticsEvent(emoji, false);
    if (analyticsEvent) {
      pushToGTMDataLayer(analyticsEvent, {
        conversationId,
        messageId,
        emoji,
        timestamp: new Date().toISOString(),
      });
    }

    return response as AxiosResponse<ConversationMessageReactionDto>;
  } catch (error) {
    useNotificationStore.getState().addNotify({
      type: 'error',
      dismissible: true,
      autoDismiss: true,
      title: 'Error',
      message: 'Failed to add reaction. Please try again.',
    });

    throw error;
  }
};

/**
 * Remove emoji reaction from a message
 */
const removeReaction = async (
  { person: personStore }: RootStoreProps,
  conversationId: string,
  messageId: string,
  emoji: string
) => {
  try {
    await personStore.waitUntilLoggedIn();

    const response = await API.delete(
      API_ENDPOINTS.ConversationMessageReactionsDelete(
        conversationId,
        messageId
      ),
      {
        data: { reaction: emoji },
      }
    );

    // Analytics tracking
    const analyticsEvent = getReactionAnalyticsEvent(emoji, true);
    if (analyticsEvent) {
      pushToGTMDataLayer(analyticsEvent, {
        conversationId,
        messageId,
        emoji,
        timestamp: new Date().toISOString(),
      });
    }

    return response as AxiosResponse<ConversationMessageReactionDto>;
  } catch (error) {
    useNotificationStore.getState().addNotify({
      type: 'error',
      dismissible: true,
      autoDismiss: true,
      title: 'Error',
      message: 'Failed to remove reaction. Please try again.',
    });

    throw error;
  }
};

/**
 * Hook to toggle emoji reaction (add if doesn't exist, remove if exists)
 */
export const useToggleReactionMutation = () => {
  const rootStore = React.useContext<RootStoreProps>(MobXProviderContext);
  const queryClient = useQueryClient();
  const updateMessageCache = useUpdateMessageCache();

  return useMutation({
    mutationKey: [MUTATION_KEY_TOGGLE_REACTION],
    mutationFn: async ({
      conversationId,
      messageId,
      emoji,
      hasReaction,
    }: ToggleReactionParams) => {
      if (hasReaction) {
        return await removeReaction(
          rootStore,
          conversationId,
          messageId,
          emoji
        );
      } else {
        return await addReaction(rootStore, conversationId, messageId, emoji);
      }
    },
    onSuccess: (response) => {
      // Use POST/DELETE response directly to avoid extra request and improve UX
      const updatedMessage = response.data;

      // Update React Query cache
      queryClient.setQueryData(
        ['message', updatedMessage.conversationId, updatedMessage.id],
        updatedMessage
      );

      // Factory to update MobX stores (grouped messages and pinned messages)
      updateMessageCache(updatedMessage);
    },
  });
};